import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { api, formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function AdminLogin() {
    const navigate = useNavigate();
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        setError("");
        setLoading(true);
        try {
            const { data } = await api.post("/auth/login", { email, password });
            localStorage.setItem("pv_token", data.token);
            navigate("/admin");
        } catch (err) {
            setError(formatApiError(err));
        } finally {
            setLoading(false);
        }
    };

    const fieldCls = "bg-white border-slate-300 text-indigo-950 h-12 rounded-none focus-visible:ring-2 focus-visible:ring-purple-500 focus-visible:ring-offset-2 focus-visible:ring-offset-white placeholder:text-slate-600";

    return (
        <div className="min-h-screen bg-white flex items-center justify-center px-6 pv-noise relative" data-testid="admin-login-page">
            <div className="absolute inset-0 pv-stars opacity-40 pointer-events-none" />
            <motion.div
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
                className="relative w-full max-w-md border border-indigo-100 bg-white shadow-[0_8px_40px_rgba(79,70,229,0.08)] p-10 sm:p-12"
            >
                <div className="mb-10">
                    <img src="/assets/logo.png" alt="Planet Valet logo" className="h-16 w-auto" />
                </div>
                <h1 className="font-serif-display text-3xl tracking-tight text-indigo-950 mb-2" data-testid="admin-login-heading">Owner login</h1>
                <p className="text-sm font-light text-slate-600 mb-8">View and manage your booking requests.</p>
                <form onSubmit={submit} className="space-y-6" data-testid="admin-login-form">
                    <div className="space-y-2">
                        <Label htmlFor="al-email" className="text-xs tracking-[0.2em] uppercase font-semibold text-slate-600">Email</Label>
                        <Input id="al-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className={fieldCls} data-testid="admin-email-input" />
                    </div>
                    <div className="space-y-2">
                        <Label htmlFor="al-password" className="text-xs tracking-[0.2em] uppercase font-semibold text-slate-600">Password</Label>
                        <Input id="al-password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} className={fieldCls} data-testid="admin-password-input" />
                    </div>
                    {error && <p className="text-sm text-red-600" data-testid="admin-login-error">{error}</p>}
                    <motion.button
                        whileTap={{ scale: 0.98 }}
                        type="submit"
                        disabled={loading}
                        className="w-full bg-blue-600 hover:bg-blue-700 disabled:opacity-60 text-white text-sm tracking-[0.15em] uppercase font-bold py-4 transition-colors duration-300"
                        data-testid="admin-login-submit-button"
                    >
                        {loading ? "Signing in…" : "Sign in"}
                    </motion.button>
                </form>
                <Link to="/" className="block mt-8 text-xs tracking-[0.2em] uppercase font-semibold text-slate-500 hover:text-purple-600 transition-colors" data-testid="admin-back-home-link">
                    ← Back to site
                </Link>
            </motion.div>
        </div>
    );
}
